import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { FiTrash2 } from 'react-icons/fi';
import Button from '../../components/Button/Button';
import ButtonSecondary from '../../components/Button/ButtonSecondary';
import IndentedDropdown from '../../components/IndentedDropdown/IndentedDropdown';
import { countryNameFromCode } from '../../data/countries';
import {
  getSites,
  getParties,
  getSite,
  getUser,
  getProductsBySupplier,
  getPurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrder
} from '../../api';
import { printPurchaseOrder, downloadPurchaseOrder } from './pdfActions';
import './_styles.purchase-orders.scss';

const today = () => new Date().toISOString().split('T')[0];

// Compose the default delivery address from a site's fields, skipping nulls.
const defaultAddress = (site) => {
  if (!site) return '';
  return [
    site.address_1,
    site.address_2,
    site.city,
    site.province,
    site.post_code,
    countryNameFromCode(site.country_code)
  ]
    .filter((part) => part && String(part).trim())
    .join(', ');
};

const lineTotal = (line) =>
  Number(line.quantity || 0) * Number(line.unit_price || 0);

const PurchaseOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const profile = JSON.parse(localStorage.getItem('profile'));

  const [sites, setSites] = useState([]);
  const [parties, setParties] = useState([]);
  const [siteDetails, setSiteDetails] = useState(null);

  const [supplierId, setSupplierId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [docNo, setDocNo] = useState('');
  const [date, setDate] = useState(today());
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState(0);
  const [number, setNumber] = useState('');
  const [lines, setLines] = useState([]);

  // Contact details
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');

  const [saving, setSaving] = useState(false);
  const editable = status === 0 || status === 1;

  // Load dropdowns + (in edit mode) the existing PO.
  useEffect(() => {
    getSites(profile.partyId).then(({ data }) =>
      setSites(data.map((s) => ({ key: s.name, value: s.id })))
    );
    getParties(true).then(({ data }) =>
      setParties(data.map((p) => ({ key: p.name, value: p.id })))
    );

    if (id) {
      loadExisting();
    } else {
      // Default contact details from the logged-in user.
      getUser(profile.userId).then(({ data }) => {
        setContactName(`${data.firstName || ''} ${data.lastName || ''}`.trim());
        setPhone(data.phone || '');
        setEmail(data.email || '');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadExisting = async () => {
    const { data } = await getPurchaseOrder(id);
    setNumber(data.number || '');
    setDocNo(data.doc_no || '');
    setSupplierId(data.supplier_id);
    setSiteId(data.to_site_id);
    setDate(data.date || today());
    setExpectedDate(data.expected_date || '');
    setNotes(data.notes || '');
    setStatus(data.status);
    setContactName(data.contact_name || '');
    setPhone(data.phone || '');
    setEmail(data.email || '');
    setLines(
      (data.lines || []).map((l) => ({
        product_id: l.product_id,
        short_name: l.product ? l.product.short_name : `#${l.product_id}`,
        quantity: l.quantity,
        unit_price: l.unit_price ?? ''
      }))
    );

    const site = await getSite(data.to_site_id);
    setSiteDetails(site.data);
    setAddress(
      data.address_override ? data.address || '' : defaultAddress(site.data)
    );
  };

  const handleSiteChange = async (e) => {
    const value = e.target.value;
    setSiteId(value);
    if (!value || value === '-1') {
      setSiteDetails(null);
      setAddress('');
      return;
    }
    const { data } = await getSite(value);
    setSiteDetails(data);
    setAddress(defaultAddress(data)); // reset to the new site's default
  };

  // Lines are supplier-specific, so switching supplier clears the products.
  const handleSupplierChange = (e) => {
    const value = e.target.value;
    if (!editable) return;
    if (String(value) === String(supplierId)) return;
    if (
      lines.length > 0 &&
      !window.confirm(
        'Changing the supplier will remove the products already added. Continue?'
      )
    ) {
      return;
    }
    setSupplierId(value);
    setLines([]);
  };

  const loadProducts = async (input) => {
    if (!supplierId) return [];
    const { data } = await getProductsBySupplier(supplierId, input);
    return data.map((p) => ({ label: p.short_name, value: p }));
  };

  const addLine = (option) => {
    if (!option) return;
    const product = option.value;
    if (lines.some((l) => l.product_id === product.id)) {
      alert('Product already added.');
      return;
    }
    setLines([
      ...lines,
      {
        product_id: product.id,
        short_name: product.short_name,
        quantity: 1,
        unit_price: ''
      }
    ]);
  };

  const updateLine = (index, field, value) => {
    setLines(
      lines.map((l, i) => (i === index ? { ...l, [field]: value } : l))
    );
  };

  const removeLine = (index) => {
    setLines(lines.filter((_, i) => i !== index));
  };

  const grandTotal = lines.reduce((sum, l) => sum + lineTotal(l), 0);

  const handlePrint = async () => {
    try {
      await printPurchaseOrder(id);
    } catch (err) {
      console.error('Failed to generate PDF', err);
      alert('Could not generate the PDF.');
    }
  };

  const handleDownload = async () => {
    try {
      await downloadPurchaseOrder(id, number);
    } catch (err) {
      console.error('Failed to download PDF', err);
      alert('Could not download the PDF.');
    }
  };

  const handleSave = async () => {
    if (!supplierId || supplierId === '-1') {
      alert('Please select a supplier.');
      return;
    }
    if (!siteId || siteId === '-1') {
      alert('Please select a site.');
      return;
    }
    if (lines.length === 0) {
      alert('Please add at least one product.');
      return;
    }
    if (lines.some((l) => !l.quantity || Number(l.quantity) <= 0)) {
      alert('Every line needs a quantity greater than zero.');
      return;
    }

    // Detect an overridden delivery address vs the site default.
    const def = defaultAddress(siteDetails);
    const trimmed = (address || '').trim();
    const addressOverride = trimmed && trimmed !== def.trim() ? 1 : 0;

    const payload = {
      supplier_id: supplierId,
      to_site_id: siteId,
      doc_no: (docNo || '').trim() || null,
      date,
      expected_date: expectedDate || null,
      notes: notes || null,
      contact_name: contactName || null,
      phone: phone || null,
      email: email || null,
      address: addressOverride ? trimmed : null,
      address_override: addressOverride,
      lines: lines.map((l) => ({
        product_id: l.product_id,
        quantity: l.quantity,
        unit_price: l.unit_price === '' ? null : l.unit_price
      }))
    };

    setSaving(true);
    try {
      if (id) {
        await updatePurchaseOrder(id, payload);
      } else {
        await createPurchaseOrder(payload);
      }
      navigate('/purchaseorders');
    } catch (err) {
      const message =
        err.response?.data?.message || 'Error saving purchase order.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='purchase-orders'>
      <div className='po-header'>
        <h3>
          {id
            ? `Purchase Order ${number || `#${id}`}`
            : 'New Purchase Order'}
        </h3>
        <div className='po-header-actions'>
          {id && <ButtonSecondary title='Print' onClick={handlePrint} />}
          {id && <ButtonSecondary title='Download' onClick={handleDownload} />}
          <ButtonSecondary
            title='Back'
            onClick={() => navigate('/purchaseorders')}
          />
        </div>
      </div>

      {!editable && (
        <div className='po-locked'>
          This purchase order is locked and can no longer be edited.
        </div>
      )}

      <div className='po-form'>
        <div className='po-section'>
          <h4 className='po-section-title'>Order Details</h4>
          <div className='form-row'>
            <div className='field'>
              <label>Site</label>
              <IndentedDropdown
                icon='pin'
                placeholder='Select a site'
                options={sites}
                value={siteId || -1}
                onChange={handleSiteChange}
                style={{ maxWidth: '100%' }}
              />
            </div>
            <div className='field'>
              <label>Supplier</label>
              <IndentedDropdown
                icon='user'
                placeholder='Select a supplier'
                options={parties}
                value={supplierId || -1}
                onChange={handleSupplierChange}
                style={{ maxWidth: '100%' }}
              />
            </div>
          </div>

          <div className='form-row'>
            <div className='field'>
              <label>Vendor Document No</label>
              <input
                type='text'
                value={docNo}
                disabled={!editable}
                onChange={(e) => setDocNo(e.target.value)}
              />
            </div>
          </div>

          <div className='form-row'>
            <div className='field'>
              <label>Document Date</label>
              <input
                type='date'
                value={date}
                disabled={!editable}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className='field'>
              <label>Expected Delivery Date</label>
              <input
                type='date'
                value={expectedDate}
                disabled={!editable}
                onChange={(e) => setExpectedDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className='po-section'>
          <h4 className='po-section-title'>Contact Details</h4>
          <div className='form-row'>
            <div className='field'>
              <label>Contact Person</label>
              <input
                type='text'
                value={contactName}
                disabled={!editable}
                onChange={(e) => setContactName(e.target.value)}
              />
            </div>
            <div className='field'>
              <label>Phone</label>
              <input
                type='text'
                value={phone}
                disabled={!editable}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>
          <div className='form-row'>
            <div className='field'>
              <label>Email</label>
              <input
                type='email'
                value={email}
                disabled={!editable}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div className='field'>
            <div className='field-header'>
              <label>Delivery Address</label>
              {editable &&
                (address || '').trim() !==
                  defaultAddress(siteDetails).trim() && (
                  <button
                    type='button'
                    className='link-reset'
                    onClick={() => setAddress(defaultAddress(siteDetails))}
                  >
                    Reset to Site Address
                  </button>
                )}
            </div>
            <textarea
              rows={3}
              value={address}
              disabled={!editable}
              onChange={(e) => setAddress(e.target.value)}
              placeholder='Defaults to the selected site address'
            />
          </div>
        </div>

        <div className='po-section'>
          <h4 className='po-section-title'>Notes</h4>
          <div className='field'>
            <textarea
              rows={3}
              value={notes}
              disabled={!editable}
              onChange={(e) => setNotes(e.target.value)}
              placeholder='Optional notes for this order'
            />
          </div>
        </div>

        <div className='po-section'>
          <h4 className='po-section-title'>Products</h4>
          <div className='field'>
            <label>Add Product</label>
            <AsyncSelect
              key={supplierId} /* reset options cache when supplier changes */
              placeholder={
                supplierId && siteId
                  ? 'Search products from this supplier'
                  : 'Select a site and supplier first'
              }
              isDisabled={!supplierId || !siteId || !editable}
              loadOptions={loadProducts}
              defaultOptions
              onChange={addLine}
              value={null}
            />
          </div>

          <div className='po-lines'>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Line Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={5} className='empty'>
                    No products added.
                  </td>
                </tr>
              ) : (
                lines.map((line, index) => (
                  <tr key={line.product_id}>
                    <td>{line.short_name}</td>
                    <td>
                      <input
                        type='number'
                        min='0'
                        step='1'
                        value={line.quantity}
                        disabled={!editable}
                        onChange={(e) =>
                          updateLine(index, 'quantity', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type='number'
                        min='0'
                        step='0.01'
                        placeholder='0.00'
                        value={line.unit_price}
                        disabled={!editable}
                        onChange={(e) =>
                          updateLine(index, 'unit_price', e.target.value)
                        }
                      />
                    </td>
                    <td>R {lineTotal(line).toFixed(2)}</td>
                    <td>
                      {editable && (
                        <button
                          className='remove'
                          onClick={() => removeLine(index)}
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {lines.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3} className='total-label'>
                    Total
                  </td>
                  <td className='total-value'>R {grandTotal.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
          </div>
        </div>

        {editable && (
          <div className='po-actions'>
            <Button
              title={saving ? 'Saving...' : id ? 'Save' : 'Create'}
              onClick={handleSave}
              disabled={saving}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrder;
